import { z } from 'zod'

import { listQueryZodFields } from './system'
import {
  OrganizationIntegrationStatus,
  OrganizationIntegrationType,
} from '../constants/organizationIntegration'
import { dbEntitySchema } from './system'

const hasConfiguredSecret = (value: unknown): boolean => {
  return typeof value === 'string' && value.trim().length > 0
}

const hasEncryptedSecret = (value: unknown): boolean => {
  return typeof value === 'string' && value.trim().startsWith('v1:')
}

const requiresSecret = <T extends z.ZodRawShape>(
  shape: T,
  secretKey: keyof T,
  encryptedKey: keyof T
) => {
  return z.object(shape).superRefine((value, ctx) => {
    const secret = value[secretKey]
    const encryptedSecret = value[encryptedKey]
    if (!hasConfiguredSecret(secret) && !hasEncryptedSecret(encryptedSecret)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${String(secretKey)} is required`,
        path: [secretKey as string],
      })
    }
  })
}

// Define reusable integration type schemas
const googleTagManagerSchema = z.object({
  containerId: z.string(),
})

const facebookSchema = z.object({
  pixelId: z.string().nullish(),
})

// Express Connect uses Account Links (platform secret key) — no OAuth tokens.
// Standard OAuth tokens remain optional for legacy integrations.
const stripeSchema = z.object({
  accountId: z.string().min(1),
  refreshToken: z.string().nullish(),
  refreshTokenEncrypted: z.string().nullish(),
  accessToken: z.string().nullish(),
  accessTokenEncrypted: z.string().nullish(),
})

const mailchimpSchema = requiresSecret(
  {
    key: z.string().nullish(),
    keyEncrypted: z.string().nullish(),
    listId: z.string(),
  },
  'key',
  'keyEncrypted'
)

const insightlySchema = requiresSecret(
  {
    key: z.string().nullish(),
    keyEncrypted: z.string().nullish(),
  },
  'key',
  'keyEncrypted'
)

const heliosSchema = requiresSecret(
  {
    key: z.string().nullish(),
    keyEncrypted: z.string().nullish(),
  },
  'key',
  'keyEncrypted'
)

const rpowerSchema = z
  .object({
    cg: z.string().nullish(),
    cgEncrypted: z.string().nullish(),
    mid: z.string().nullish(),
    midEncrypted: z.string().nullish(),
  })
  .superRefine((value, ctx) => {
    if (
      !hasConfiguredSecret(value.cg) &&
      !hasEncryptedSecret(value.cgEncrypted)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'cg is required',
        path: ['cg'],
      })
    }
    if (
      !hasConfiguredSecret(value.mid) &&
      !hasEncryptedSecret(value.midEncrypted)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'mid is required',
        path: ['mid'],
      })
    }
  })

const silverwareSchema = requiresSecret(
  {
    token: z.string().nullish(),
    tokenEncrypted: z.string().nullish(),
    baseUrl: z.string(),
  },
  'token',
  'tokenEncrypted'
)

const omnivoreSchema = z.object({
  posLocationId: z.string(),
})

export const toastPublicSchema = z.object({
  accessType: z.literal('standard_api').optional().nullable(),
  clientId: z.string().optional().nullable(),
  clientSecret: z.string().optional().nullable(),
  clientSecretEncrypted: z.string().optional().nullable(),
  clientSecretConfigured: z.boolean().optional(),
  restaurantGuid: z.string().optional().nullable(),
  managementGroupGuid: z.string().optional().nullable(),
  autoLinkOrders: z.boolean().optional(),
})

export const toastInputSchema = z.object({
  accessType: z.literal('standard_api').optional().nullable(),
  clientId: z.string().nullish(),
  clientSecret: z.string().nullish(),
  clientSecretEncrypted: z.string().nullish(),
  restaurantGuid: z.string().nullish(),
  autoLinkOrders: z.boolean().optional(),
})

const toastActiveValidationSchema = z
  .object({
    accessType: z.literal('standard_api').optional().nullable(),
    clientId: z.string().trim().min(1),
    clientSecret: z.string().nullish(),
    clientSecretEncrypted: z.string().nullish(),
    restaurantGuid: z.string().trim().min(1),
    managementGroupGuid: z.string().optional().nullable(),
    autoLinkOrders: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (
      !hasConfiguredSecret(value.clientSecret) &&
      !hasEncryptedSecret(value.clientSecretEncrypted)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'clientSecret is required',
        path: ['clientSecret'],
      })
    }
  })

export const squarePublicSchema = z.object({
  accessToken: z.string().optional().nullable(),
  accessTokenEncrypted: z.string().optional().nullable(),
  accessTokenConfigured: z.boolean().optional(),
  locationId: z.string().optional().nullable(),
  environment: z.enum(['production', 'sandbox']).optional().nullable(),
})

export const squareInputSchema = z.object({
  accessToken: z.string().nullish(),
  accessTokenEncrypted: z.string().nullish(),
  locationId: z.string().nullish(),
  environment: z.enum(['production', 'sandbox']).nullish(),
})

const squareActiveValidationSchema = z
  .object({
    accessToken: z.string().nullish(),
    accessTokenEncrypted: z.string().nullish(),
    locationId: z.string().trim().min(1),
    environment: z.enum(['production', 'sandbox']).optional().nullable(),
  })
  .superRefine((value, ctx) => {
    if (
      !hasConfiguredSecret(value.accessToken) &&
      !hasEncryptedSecret(value.accessTokenEncrypted)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'accessToken is required',
        path: ['accessToken'],
      })
    }
  })

const billfoldSchema = z.object({
  key: z.string().optional().nullable(),
  keyEncrypted: z.string().optional().nullable(),
  webhookSecret: z.string().optional().nullable(),
  webhookSecretEncrypted: z.string().optional().nullable(),
})

const aiqSchema = requiresSecret(
  {
    apiKey: z.string().nullish(),
    apiKeyEncrypted: z.string().nullish(),
    uid: z.string(),
  },
  'apiKey',
  'apiKeyEncrypted'
)

// Define the validation schema with required fields
export const organizationIntegrationValidationSchema = z.discriminatedUnion(
  'type',
  [
    z.object({
      type: z.literal(OrganizationIntegrationType.FACEBOOK),
      facebook: facebookSchema.optional().nullable(),
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.GOOGLE_TAG_MANAGER),
      googleTagManager: googleTagManagerSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.STRIPE),
      stripe: stripeSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.MAILCHIMP),
      mailchimp: mailchimpSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.INSIGHTLY),
      insightly: insightlySchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.HELIOS),
      helios: heliosSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.RPOWER),
      rpower: rpowerSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.SILVERWARE),
      silverware: silverwareSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.OMNIVORE),
      omnivore: omnivoreSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.TOAST),
      toast: toastActiveValidationSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.SQUARE),
      square: squareActiveValidationSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.BILLFOLD),
      billfold: billfoldSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.AIQ),
      aiq: aiqSchema,
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.TABLELIST),
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.DISCOTECH),
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.CYMBAL),
    }),
    z.object({
      type: z.literal(OrganizationIntegrationType.SWIVUL),
    }),
  ]
)

/** Wire/document shape: Mongo often stores explicit nulls on subdocument fields. */
const insightlyDocumentSchema = z.object({
  key: z.string().nullish(),
  keyEncrypted: z.string().nullish(),
  keyConfigured: z.boolean().optional(),
})
const mailchimpDocumentSchema = z.object({
  key: z.string().nullish(),
  keyEncrypted: z.string().nullish(),
  keyConfigured: z.boolean().optional(),
  listId: z.string().nullish(),
})
const stripeDocumentSchema = z.object({
  accountId: z.string().nullish(),
  refreshToken: z.string().nullish(),
  refreshTokenEncrypted: z.string().nullish(),
  refreshTokenConfigured: z.boolean().optional(),
  accessToken: z.string().nullish(),
  accessTokenEncrypted: z.string().nullish(),
  accessTokenConfigured: z.boolean().optional(),
})
const heliosDocumentSchema = z.object({
  key: z.string().nullish(),
  keyEncrypted: z.string().nullish(),
  keyConfigured: z.boolean().optional(),
})
const rpowerDocumentSchema = z.object({
  cg: z.string().nullish(),
  cgEncrypted: z.string().nullish(),
  cgConfigured: z.boolean().optional(),
  mid: z.string().nullish(),
  midEncrypted: z.string().nullish(),
  midConfigured: z.boolean().optional(),
})
const googleTagManagerDocumentSchema = z.object({
  containerId: z.string().nullish(),
})
const facebookDocumentSchema = z.object({
  pixelId: z.string().nullish(),
})
const silverwareDocumentSchema = z.object({
  token: z.string().nullish(),
  tokenEncrypted: z.string().nullish(),
  tokenConfigured: z.boolean().optional(),
  baseUrl: z.string().nullish(),
})
const omnivoreDocumentSchema = z.object({
  posLocationId: z.string().nullish(),
})
const billfoldDocumentSchema = z.object({
  key: z.string().nullish(),
  keyEncrypted: z.string().nullish(),
  keyConfigured: z.boolean().optional(),
  webhookSecret: z.string().nullish(),
  webhookSecretEncrypted: z.string().nullish(),
  webhookSecretConfigured: z.boolean().optional(),
})
const aiqDocumentSchema = z.object({
  apiKey: z.string().nullish(),
  apiKeyEncrypted: z.string().nullish(),
  apiKeyConfigured: z.boolean().optional(),
  uid: z.string().nullish(),
})

const organizationIntegrationTypeDataInputSchema = dbEntitySchema.extend({
  insightly: insightlyDocumentSchema
    .omit({ keyConfigured: true })
    .optional()
    .nullable(),
  mailchimp: mailchimpDocumentSchema
    .omit({ keyConfigured: true })
    .optional()
    .nullable(),
  stripe: stripeDocumentSchema
    .omit({ refreshTokenConfigured: true, accessTokenConfigured: true })
    .optional()
    .nullable(),
  helios: heliosDocumentSchema
    .omit({ keyConfigured: true })
    .optional()
    .nullable(),
  rpower: rpowerDocumentSchema
    .omit({ cgConfigured: true, midConfigured: true })
    .optional()
    .nullable(),
  facebook: facebookDocumentSchema.optional().nullable(),
  googleTagManager: googleTagManagerDocumentSchema.optional().nullable(),
  silverware: silverwareDocumentSchema
    .omit({ tokenConfigured: true })
    .optional()
    .nullable(),
  omnivore: omnivoreDocumentSchema.optional().nullable(),
  toast: toastPublicSchema
    .omit({ clientSecretEncrypted: true })
    .optional()
    .nullable(),
  square: squarePublicSchema
    .omit({ accessTokenEncrypted: true })
    .optional()
    .nullable(),
  billfold: billfoldDocumentSchema
    .omit({ keyConfigured: true, webhookSecretConfigured: true })
    .optional()
    .nullable(),
  aiq: aiqDocumentSchema.omit({ apiKeyConfigured: true }).optional().nullable(),
})

const organizationIntegrationInputSchema =
  organizationIntegrationTypeDataInputSchema.extend({
    organization: z.string(),
    venue: z.string(),
    type: z.nativeEnum(OrganizationIntegrationType),
    status: z.nativeEnum(OrganizationIntegrationStatus),
  })

// Define the type data schema with optional nullable fields
export const organizationIntegrationTypeDataSchema = dbEntitySchema.extend({
  insightly: insightlyDocumentSchema.optional().nullable(),
  mailchimp: mailchimpDocumentSchema.optional().nullable(),
  stripe: stripeDocumentSchema.optional().nullable(),
  helios: heliosDocumentSchema.optional().nullable(),
  rpower: rpowerDocumentSchema.optional().nullable(),
  facebook: facebookDocumentSchema.optional().nullable(),
  googleTagManager: googleTagManagerDocumentSchema.optional().nullable(),
  silverware: silverwareDocumentSchema.optional().nullable(),
  omnivore: omnivoreDocumentSchema.optional().nullable(),
  toast: toastPublicSchema.optional().nullable(),
  square: squarePublicSchema.optional().nullable(),
  billfold: billfoldDocumentSchema.optional().nullable(),
  aiq: aiqDocumentSchema.optional().nullable(),
})

export const organizationIntegrationSchema =
  organizationIntegrationTypeDataSchema.extend({
    organization: z.string(),
    venue: z.string(),
    type: z.nativeEnum(OrganizationIntegrationType),
    status: z.nativeEnum(OrganizationIntegrationStatus),
  })

export const orgIntsListArgsSchema = z.object({
  ...listQueryZodFields,
  organization: z.string().optional(),
  venueId: z.array(z.string()).or(z.string()).optional(),
  status: z.nativeEnum(OrganizationIntegrationStatus).optional(),
  type: z.nativeEnum(OrganizationIntegrationType).optional(),
})
export const orgIntCreateArgsSchema = organizationIntegrationInputSchema
  .omit({
    id: true,
    _id: true,
    deleted: true,
    created: true,
    modified: true,
    organization: true,
    venue: true,
    status: true,
  })
  .extend({
    venueId: z.string(),
    status: z.nativeEnum(OrganizationIntegrationStatus).optional(),
    toast: toastInputSchema.optional().nullable(),
    square: squareInputSchema.optional().nullable(),
  })
export const orgIntUpdateByIdArgsSchema = organizationIntegrationSchema
  .omit({
    insightly: true,
    mailchimp: true,
    stripe: true,
    helios: true,
    rpower: true,
    silverware: true,
    toast: true,
    square: true,
    billfold: true,
    aiq: true,
  })
  .extend({
    insightly: insightlyDocumentSchema
      .omit({ keyConfigured: true })
      .optional()
      .nullable(),
    mailchimp: mailchimpDocumentSchema
      .omit({ keyConfigured: true })
      .optional()
      .nullable(),
    stripe: stripeDocumentSchema
      .omit({ refreshTokenConfigured: true, accessTokenConfigured: true })
      .optional()
      .nullable(),
    helios: heliosDocumentSchema
      .omit({ keyConfigured: true })
      .optional()
      .nullable(),
    rpower: rpowerDocumentSchema
      .omit({ cgConfigured: true, midConfigured: true })
      .optional()
      .nullable(),
    silverware: silverwareDocumentSchema
      .omit({ tokenConfigured: true })
      .optional()
      .nullable(),
    toast: toastPublicSchema
      .omit({ clientSecretConfigured: true, managementGroupGuid: true })
      .optional()
      .nullable(),
    square: squarePublicSchema
      .omit({ accessTokenConfigured: true })
      .optional()
      .nullable(),
    billfold: billfoldDocumentSchema
      .omit({ keyConfigured: true, webhookSecretConfigured: true })
      .optional()
      .nullable(),
    aiq: aiqDocumentSchema
      .omit({ apiKeyConfigured: true })
      .optional()
      .nullable(),
  })
  .partial()
  .omit({
    id: true,
    _id: true,
    deleted: true,
    created: true,
    modified: true,
    organization: true,
    venue: true,
  })

//responses
export const orgIntsListResponseSchema = z.object({
  data: z.array(organizationIntegrationSchema),
})

export const orgIntSingleResponseSchema = z.object({
  data: organizationIntegrationSchema,
})

export const integrationToastConnectionTestResponseSchema = z.object({
  data: organizationIntegrationSchema,
})

export const toastRestaurantSchema = z.object({
  guid: z.string(),
  name: z.string().optional().nullable(),
  locationName: z.string().optional().nullable(),
})

export const toastRestaurantsListQuerySchema = z.object({
  bootstrapRestaurantGuid: z.string().trim().min(1).optional(),
})

export const toastRestaurantsListResponseSchema = z.object({
  data: z.array(toastRestaurantSchema),
})

export const toastRestaurantsPreviewRequestSchema = z.object({
  clientId: z.string().trim().min(1),
  clientSecret: z.string().trim().min(1),
  bootstrapRestaurantGuid: z.string().trim().min(1),
})

export const organizationIntegrationCatalogEntrySchema = z.object({
  slug: z.string(),
  shortDescription: z.string(),
  about: z.string(),
  blogUrl: z.string().url().optional(),
  logoUrl: z.string().url(),
})
